import React, { useState, useEffect, useRef, useCallback } from "react";
import Para from "@/atoms/text/Para";
import H4 from "@/atoms/text/H4";
import H2 from "@/atoms/text/H2";
import Image from "@/components/atoms/Image";
import { isMobileScreen } from "@/utils/functions";
import PropTypes from "prop-types";

export default function WhyIntegrate({ title, contentData, image }) {
  const [isMobile, setIsMobile] = useState(null);
  useEffect(() => {
    setIsMobile(isMobileScreen);
  }, []);

  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef(null);

  const startInterval = useCallback(() => {
    clearInterval(intervalRef.current); // Clear previous interval
    intervalRef.current = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % contentData.length);
    }, 5000);
  }, [contentData]);

  useEffect(() => {
    startInterval(); // Start the interval initially and on contentData changes
    return () => clearInterval(intervalRef.current);
  }, [startInterval]);

  const handleClick = (index) => {
    setActiveIndex(index);
    clearInterval(intervalRef.current); // Clear interval on click
    startInterval();
  };

  return (
    <section className="bc-blue-light pv-15-d pv-8">
      <div className="container">
        <H2
          desktopSize="lg"
          mobileSize="sm"
          className="ta-center-d bold mb-10-d mb-7"
        >
          {title}
        </H2>
        <article className={`fx-d fx--sb-d ${isMobile ? "" : "fx--cv"}`}>
          <div className="w-50-d pl-10-d fx--order-2-d">
            {contentData.map((item, idx) => {
              const contentClassName = isMobile
                ? ""
                : idx === activeIndex
                ? "active"
                : "";

              return (
                <div
                  key={item.index}
                  className={`${
                    isMobile ? "" : "shift-text"
                  } ${contentClassName}`}
                  onClick={() => !isMobile && handleClick(idx)}
                  tabIndex={isMobile ? undefined : idx}
                >
                  <H4 desktopSize="xs" mobileSize="xxs" className="bold mb-1">
                    {item.title}
                  </H4>
                  <Para
                    desktopSize="lg"
                    mobileSize="lg"
                    className={
                      idx === contentData.length - 1 && !isMobile
                        ? ""
                        : "mb-8-d mb-7"
                    }
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  ></Para>
                  <style jsx>{`
                    .shift-text {
                      position: relative;
                      transition: all 0.4s cubic-bezier(0.165, 0.85, 0.45, 1);
                      opacity: 0.5;
                      cursor: pointer;
                    }

                    .shift-text.active {
                      padding-left: 20px;
                      opacity: 1;
                    }

                    .shift-text.active:after {
                      content: "";
                      width: 2px;
                      height: 100%;
                      background: #fbaf3b;
                      position: absolute;
                      left: 0;
                      top: 0;
                    }
                  `}</style>
                </div>
              );
            })}
          </div>
          <Image
            fileName={image.fileName}
            desktopWidth={image.desktopWidth}
            mobileWidth={image.mobileWidth}
            alt={image.altText}
            className="fx--order-1-d"
            fileFormat="png"
            imgStyle="m-center"
          />
        </article>
      </div>
    </section>
  );
}

WhyIntegrate.propTypes = {
  title: PropTypes.string,
  contentData: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string,
    })
  ),
  image: PropTypes.shape({
    fileName: PropTypes.string,
    desktopWidth: PropTypes.number,
    mobileWidth: PropTypes.number,
    altText: PropTypes.string,
  }),
};
